import { Paper } from "@mui/material";
import { Model } from "survey-core";

import CondensedReportSurvey from "./CondensedReportSurvey";
import cardStyles from "./CondensedReportView.module.scss";
import styles from "./ReviewerDetails.module.scss";
import SectionHeader from "./SectionHeader";

export interface ReviewerDetailsProps {
  /**
   * The reviewer-details SurveyJS model, built from the survey's
   * `reviewer_details_survey` config via `buildReviewerDetailsModel`. Only some
   * surveys configure this section; renders nothing when the model is absent.
   */
  model?: Model | null;
}

/**
 * Optional reviewer sign-off section at the bottom of the condensed report.
 * Some surveys configure it via `reviewer_details_survey`; the caller builds the
 * model (with `buildReviewerDetailsModel`) and owns data persistence. Renders
 * nothing when no model is configured.
 *
 * The shared SectionHeader tops the card, followed by a line of static helper
 * text and then the condensed survey (which brings its own section panel below).
 *
 * Reviewer details have their own edit state, driven by the model's mode rather
 * than the main report's edit toggle: the caller builds the model in edit mode
 * while the report is open and display mode once it's closed (approved /
 * rejected / escalated), so the card stays editable until then and reverts to
 * read-only after. Deriving the edit chrome from the mode keeps the two in sync.
 */
const ReviewerDetails = ({ model }: ReviewerDetailsProps) => {
  if (!model) return null;

  const editing = model.mode === "edit";

  return (
    <Paper elevation={3} className={cardStyles.paperOfficerDetailsContainer}>
      <SectionHeader
        title="Reviewer Details"
        className={cardStyles.officerCardHeader}
      />
      <p className={styles.helperText}>
        Additional information to be included in the export to the records
        system. This information will not be shared with the reporting
        individual.
      </p>
      <div className={cardStyles.officerCardBody}>
        <CondensedReportSurvey model={model} editing={editing} />
      </div>
    </Paper>
  );
};

export default ReviewerDetails;
